import React, { useState, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface ActivityQuickAddProps {
  onAdd: (title: string) => Promise<void>;
  disabled?: boolean;
}

export const ActivityQuickAdd: React.FC<ActivityQuickAddProps> = ({ onAdd, disabled }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  const handleAdd = async () => {
    const trimmed = title.trim();
    if (!trimmed) return;

    setLoading(true);
    try {
      await onAdd(trimmed);
      setTitle('');
      // keep input open so user can immediately type the next activity
      setTimeout(() => inputRef.current?.focus(), 0);
    } catch {
      // keep input open on error
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
    if (e.key === 'Escape') {
      setTitle('');
      setIsAdding(false);
    }
  };

  const handleCancel = () => {
    setTitle('');
    setIsAdding(false);
  };

  if (!isAdding) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start text-gray-500 hover:text-gray-800 hover:bg-gray-50 border border-dashed border-gray-300 hover:border-gray-400"
        onClick={() => setIsAdding(true)}
        disabled={disabled}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Activity...
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2 p-2 border border-purple-300 rounded-lg bg-purple-50">
      <Plus className="h-4 w-4 text-purple-500 flex-shrink-0" />
      <Input
        ref={inputRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Activity name... (Enter to add, Esc to close)"
        className="flex-1 h-8 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-sm placeholder:text-gray-400"
        disabled={loading}
      />
      <Button
        size="sm"
        className="h-7 px-3 text-xs"
        onClick={handleAdd}
        disabled={loading || !title.trim()}
      >
        {loading ? '...' : 'Add'}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-gray-500"
        onClick={handleCancel}
        disabled={loading}
      >
        Done
      </Button>
    </div>
  );
};
